type Color = string;

export function applyOpacity(color: string, alpha = 0.3) {
	if (color.startsWith('rgb')) {
		// Convert rgb(...) or rgba(...) to rgba(...) with new alpha
		return color.replace(/rgba?\(([^)]+)\)/, (_, values) => {
			const [r, g, b] = values.split(',').map((v: string) => v.trim());
			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		});
	} else {
		// assume hex
		const sanitizedHex = color.replace('#', '');
		const bigint = parseInt(sanitizedHex, 16);
		const r = (bigint >> 16) & 255;
		const g = (bigint >> 8) & 255;
		const b = bigint & 255;
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	}
}

const parseColor = (color: Color) => {
	if (color.startsWith('#')) {
		const col = parseInt(color.slice(1), 16);
		return {
			format: 'hex',
			r: (col >> 16) & 0xff,
			g: (col >> 8) & 0xff,
			b: col & 0xff,
			a: 1,
		};
	}

	const rgbRegex = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)$/;
	const match = color.match(rgbRegex);
	if (match) {
		return {
			format: match[4] !== undefined ? 'rgba' : 'rgb',
			r: parseInt(match[1] as string),
			g: parseInt(match[2] as string),
			b: parseInt(match[3] as string),
			a: match[4] !== undefined ? parseFloat(match[4]) : 1,
		};
	}

	throw new Error('Unsupported color format');
};

const formatColor = (c: {
	r: number;
	g: number;
	b: number;
	a: number;
	format: string;
}) => {
	const { r, g, b, a, format } = c;
	if (format === 'hex') {
		return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
	}
	if (format === 'rgb') {
		return `rgb(${r}, ${g}, ${b})`;
	}
	if (format === 'rgba') {
		return `rgba(${r}, ${g}, ${b}, ${a})`;
	}
	return '';
};

export const darkenHex = (color: Color, amount: number) => {
	const c = parseColor(color);
	c.r = Math.max(c.r - amount, 0);
	c.g = Math.max(c.g - amount, 0);
	c.b = Math.max(c.b - amount, 0);
	return formatColor(c);
};

export const lightenHex = (color: Color, amount: number) => {
	const c = parseColor(color);
	c.r = Math.min(c.r + amount, 255);
	c.g = Math.min(c.g + amount, 255);
	c.b = Math.min(c.b + amount, 255);
	return formatColor(c);
};

export function getReadableTextColor(bgColor: string): '#000000' | '#FFFFFF' {
	const color = bgColor.replace('#', '');

	const r = parseInt(color.substring(0, 2), 16);
	const g = parseInt(color.substring(2, 4), 16);
	const b = parseInt(color.substring(4, 6), 16);

	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
